import type { ConversationDTO, ConversationStatus, ThreadType } from '@eriko/shared-types';
import { formatRelativeTime } from '@eriko/shared-types';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@eriko/ui/src/components/ui/dropdown-menu';
import { cn } from '@eriko/ui/src/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, MoreVertical, Plus, Sparkles, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { archiveConversation, resolveConversation } from '../../../api/conversations';
import { useConversations } from '../../../hooks/useConversations';

const TYPE_ICON: Record<ThreadType, typeof Sparkles> = {
  ERIKO_INIT: Sparkles,
  REVIEWER_INIT: User,
  SME_INIT: MessageCircle,
  REVIEWER_AI: MessageCircle,
};

const STATUS_DOT: Record<ConversationStatus, string> = {
  OPEN: 'bg-amber-500',
  NEEDS_REPLY: 'bg-red-500',
  RESOLVED: 'bg-surface-400',
  ARCHIVED: 'bg-surface-300',
};

export function ThreadSidebar() {
  const navigate = useNavigate();
  const { conversationId: activeId } = useParams<{ conversationId?: string }>();
  const { data: conversations = [], isLoading } = useConversations();

  const rows = useMemo(() => {
    return [...conversations].sort(
      (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime(),
    );
  }, [conversations]);

  const handleSelectConversation = (selectedId: string) => {
    if (selectedId === activeId) return;
    navigate(`/chat/${selectedId}`);
  };


  function handleNewConversation() {
    navigate('/chat');
  }

  return (
    <div className="w-[300px] shrink-0 bg-surface-0 border-r border-surface-300 flex flex-col">
      <div className='p-4'>
        <Button
          type="button"
          onClick={handleNewConversation}
          className='w-full'
          size="lg"
          title="New conversation"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          <span>New Chat</span>
        </Button>
      </div>
      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading && (
          <p className="px-3 py-8 text-center text-xs text-surface-500">Loading…</p>
        )}
        {!isLoading && rows.length === 0 && (
          <p className="px-3 py-8 text-center text-xs text-surface-500">No conversations.</p>
        )}
        {rows.map((conv) => (
          <ConvRow
            key={conv.id}
            conv={conv}
            active={conv.id === activeId}
            onSelect={() => handleSelectConversation(conv.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ConvRow({
  conv,
  active,
  onSelect,
}: {
  conv: ConversationDTO;
  active: boolean;
  onSelect: () => void;
}) {
  const Icon = TYPE_ICON[conv.threadType];
  const [menuOpen, setMenuOpen] = useState(false);
  const qc = useQueryClient();

  const resolve = useMutation({
    mutationFn: () => resolveConversation(conv.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', conv.id] });
    },
  });

  const archive = useMutation({
    mutationFn: () => archiveConversation(conv.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', conv.id] });
    },
  });

  const showKebab = conv.status !== 'ARCHIVED';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative w-full text-left px-3.5 py-3 rounded-lg cursor-pointer transition-colors mb-0.5',
        active ? 'bg-surface-100' : 'hover:bg-surface-100',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', STATUS_DOT[conv.status])} />
        <Icon className="w-3.5 h-3.5 text-surface-500 shrink-0" strokeWidth={1.8} />
        <span className="flex-1 text-[13px] font-semibold text-surface-900 truncate">
          {conv.title || 'New conversation'}
        </span>
        <span
          className={cn(
            'font-mono text-[10px] text-surface-500 shrink-0 transition-opacity',
            showKebab && (menuOpen ? 'opacity-0' : 'group-hover:opacity-0'),
          )}
        >
          {formatRelativeTime(conv.lastActivityAt)}
        </span>
        {showKebab && (
          <div
            className={cn(
              'absolute right-2 top-2 transition-opacity',
              menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-surface-400 hover:text-surface-600 hover:bg-surface-200"
                  title="More actions"
                >
                  <MoreVertical className="w-[16px] h-[16px]" strokeWidth={1.8} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {conv.status !== 'RESOLVED' && (
                  <DropdownMenuItem onClick={() => resolve.mutate()}>
                    Mark resolved
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => archive.mutate()}>Archive</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      {conv.lastMessagePreview && (
        <div className="mt-0.5 pl-5 text-xs text-surface-500 line-clamp-1">
          {conv.lastMessageSenderType === 'AI'
            ? ''
            : conv.lastMessageSenderType === 'REVIEWER'
              ? 'Bookkeeper: '
              : 'You: '}
          {conv.lastMessagePreview}
        </div>
      )}
      {conv.blocksClose && (
        <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800">
          Holds month-close
        </span>
      )}
    </div>
  );
}
